import React, { FC } from "react";
import { Controller, UseFormSetValue, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  CompanyEditorFieldProps,
  CompanyEditorFormData,
} from "../../CompanyEditor.types";

import { TextField, Typography, Autocomplete } from "@components/base";
import { useUserStore } from "@store/user";
import { useGetNaceSections, useGetNaceDivisions } from "@api/hooks/nace";

interface NaceClassificationProps extends CompanyEditorFieldProps {
  setValue: UseFormSetValue<CompanyEditorFormData>;
}

const NaceClassification: FC<NaceClassificationProps> = ({
  control,
  setValue,
}) => {
  const { t } = useTranslation();
  const { user } = useUserStore();

  const naceSectionId = useWatch({ control, name: "naceSectionId" });

  const { naceSections } = useGetNaceSections(user);
  const { naceDivisions } = useGetNaceDivisions(user, {
    naceSectionId: naceSectionId || "",
  });

  return (
    <>
      <Typography variant="body2" color="text.secondary">
        {t("common.naceSection")}
      </Typography>
      <Controller
        name="naceSectionId"
        control={control}
        render={({ field }) => (
          <Autocomplete
            options={naceSections ?? []}
            getOptionLabel={(option) => option.name}
            value={naceSections?.find((s) => s.id === field.value) ?? null}
            onChange={(_event, newValue) => {
              field.onChange(newValue?.id ?? "");
              setValue("naceDivisionId", null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="filled"
                placeholder={t("common.naceSection")}
              />
            )}
            fullWidth
          />
        )}
      />

      {naceSectionId && (
        <>
          <Typography variant="body2" color="text.secondary">
            {t("common.naceDivision")}
          </Typography>
          <Controller
            name="naceDivisionId"
            control={control}
            render={({ field }) => (
              <Autocomplete
                options={naceDivisions ?? []}
                getOptionLabel={(option) => option.name}
                value={naceDivisions?.find((d) => d.id === field.value) ?? null}
                onChange={(_event, newValue) => field.onChange(newValue?.id ?? null)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="filled"
                    placeholder={t("common.naceDivision")}
                  />
                )}
                fullWidth
              />
            )}
          />
        </>
      )}
    </>
  );
};

export default NaceClassification;
